'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const tiers = [
  {
    name: 'Gold',
    price: '₹5,000',
    badge: 'Most Popular',
    color: 'border-accent',
    highlight: true,
    benefits: [
      'Festival Entry Passes (All Days)',
      'Bhog Prasadam Coupons (Included)',
      'Invitation to Cultural Evening - Reserved Devotee Seating (subject to availability)',
      'All Cultural Programme Passes',
      'Name in Prathaa 2026 Souvenir',
    ],
  },
  {
    name: 'Diamond',
    price: '₹10,000',
    badge: null,
    color: 'border-border',
    highlight: false,
    benefits: [
      'Festival Entry Passes + Priority Darshan Facilitation',
      'Bhog Prasadam for Family',
      'Invitation to Cultural Evening - Reserved Patron Seating',
      'Invitation to Festival Programmes + Special Patron Invitation',
      'Prathaa Commemorative Seva Hamper',
      'Patron Acknowledgement in Festival Souvenir Programme',
    ],
  },
  {
    name: 'Platinum',
    price: '₹25,000',
    badge: 'Most Exclusive',
    color: 'border-primary',
    highlight: false,
    benefits: [
      'Reserved Patron Seating (All Days)',
      'Bhog Prasadam for Family + Extra Passes',
      'Invitation to Cultural Evening - Reserved Patron Seating / VIP',
      'Invitation to Festival Programmes + Patron Interaction with Organising Committee',
      'Patron Appreciation Interaction with Javed Ali',
      'Special Patron Acknowledgement in Souvenir',
      'Platinum Lounge + Refreshments',
    ],
  },
];

export default function MembershipPreviewSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background alpona-bg w-full px-4 md:px-12 pt-14 md:pt-20 pb-12 md:pb-16">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full border border-border text-muted-foreground text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
            Membership 2026
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-foreground tracking-tight mb-3 md:mb-4">
            Choose Your <span className="text-primary">Pass</span>
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
            Limited memberships. Every tier includes Gala Evening access at Shanmukhananda Chandrasekhara Saraswati Auditorium with Javed Ali.
          </p>
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5 mb-8 md:mb-10 max-w-5xl mx-auto">
          {tiers?.map((tier, i) => (
            <div
              key={tier?.name}
              className={`reveal bg-card border-2 ${tier?.color} rounded-3xl md:rounded-4xl p-5 md:p-7 flex flex-col relative overflow-hidden transition-all duration-300 hover:shadow-gold hover:-translate-y-1 ${tier?.highlight ? 'shadow-gold-lg' : ''}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {tier?.highlight && (
                <div className="absolute inset-0 bg-gradient-to-b from-accent/5 to-transparent pointer-events-none rounded-4xl" />
              )}
              {tier?.badge && (
                <span className={`absolute top-4 right-4 md:top-5 md:right-5 ${tier?.badge === 'Most Popular' ? 'tier-badge-popular' : 'tier-badge-exclusive'}`}>
                  {tier?.badge}
                </span>
              )}
              <div className="mb-1">
                <h3 className="font-display text-xl md:text-2xl font-black text-foreground tracking-tight">{tier?.name}</h3>
              </div>
              <div className="mb-4 md:mb-6">
                <span className="font-display text-3xl md:text-4xl font-black text-primary">{tier?.price}</span>
              </div>
              <ul className="space-y-2.5 flex-1 mb-5 md:mb-7">
                {tier?.benefits?.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-card-foreground/80">
                    <svg className="text-accent flex-shrink-0 mt-0.5" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                    {b}
                  </li>
                ))}
              </ul>
              <Link
                href="/membership"
                className={`block text-center py-3 px-6 rounded-full font-bold text-sm transition-all duration-200 ${
                  tier?.highlight
                    ? 'bg-accent text-accent-foreground hover:bg-secondary hover:text-primary-foreground shadow-gold'
                    : 'border-2 border-foreground/20 text-foreground hover:border-primary hover:text-primary'
                }`}
              >
                Select {tier?.name}
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center reveal">
          <Link
            href="/membership"
            className="inline-flex items-center gap-2 text-muted-foreground text-sm font-semibold hover:text-primary transition-colors duration-200"
          >
            View full benefits comparison →
          </Link>
        </div>
      </div>
    </section>
  );
}